import React, { useEffect, useState } from 'react';
import {
  EncephalonContainer,
  EncephalonHeaderRow,
  EncephalonLabel,
  StudyButton,
  LoadingSpinner,
  LoadingMessage,
} from './styled';
import {
  useCreateEncephalonStudyMutation,
  useGetEncephaloneStudyDataQuery,
  useUploadDicomsSequentialMutation,
} from '../../services/dicomService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useEncephalon } from '../../contexts/EncephalonContext';
import ScanSection from '../CreateScan';

export interface EncephalonPayload {
  name: string;
  age: number;
  height: number;
  weight: number;
  sex: string;
}

type Props = EncephalonPayload & {
  dicomsInStudy?: any[];
};

const EncephalonSection: React.FC<Props> = ({ name, age, height, weight, sex, dicomsInStudy }) => {
  const payload = { mrn: name, age, height, weight, sex };
  const [createStudy, { isLoading }] = useCreateEncephalonStudyMutation();
  const { setStudyExists, setCurrentStudyUuid, triggerScanUpdate, setScans } = useEncephalon();
  const [uploadDicoms, { isLoading: isUploading }] = useUploadDicomsSequentialMutation();

  const [studyReady, setStudyReady] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [createdStudyUuid, setCreatedStudyUuid] = useState<string | null>(null);

  // Fetch study existence directly here
  const { data: studyData, isFetching } = useGetEncephaloneStudyDataQuery(name, { skip: !name });

  // Detect existing study from API
  useEffect(() => {
    if (studyData && Array.isArray(studyData.results) && studyData.results.length > 0) {
      setStudyExists(true);
      setCurrentStudyUuid(studyData.results[0].uuid);
      setStudyReady(true);
      triggerScanUpdate();
      setCreatedStudyUuid(studyData.results[0].uuid);
    }
  }, [studyData, setStudyExists, setCurrentStudyUuid, triggerScanUpdate]);

  // Helper to extract signed URLs and IDs from dicomsInStudyData
  const getDicomData = () => {
    if (!dicomsInStudy || !Array.isArray(dicomsInStudy)) return [];
    return dicomsInStudy
      .map((dicom: any) => ({
        signed_url: dicom.signed_url,
        name: dicom.icid,
      }))
      .filter(dicom => dicom.signed_url);
  };

  const handleCreate = async () => {
    if (studyReady) return;

    try {
      const response = await createStudy(payload).unwrap();
      const studyUuid = response?.study?.uuid;

      if (studyUuid) {
        setCurrentStudyUuid(studyUuid);
        setCreatedStudyUuid(studyUuid);
        setScans([]);
        setStudyExists(true);
        setStudyReady(true);
        triggerScanUpdate();

        // Initiate DICOM upload after study creation
        const dicomData = getDicomData();
        if (dicomData.length > 0) {
          uploadDicoms({
            study_id: studyUuid,
            dicom_data: dicomData,
          })
            .unwrap()
            .then(() => {
              toast.success('DICOMs uploaded to Encephalon!');
            })
            .catch((err: any) => {
              toast.error('Failed to upload DICOMs: ' + (err?.data?.detail || 'Unknown error'));
            });
        }

        toast.success('Study created!');
      } else {
        console.warn('⚠️ Study UUID not found in response:', response);
      }
    } catch (err: any) {
      const detail = err?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : detail?.message || detail?.error || 'Unexpected error';
      toast.error(msg);
    }
  };

  return (
    <EncephalonContainer>
      <EncephalonHeaderRow>
        <EncephalonLabel>Encephalon</EncephalonLabel>
      </EncephalonHeaderRow>
      <StudyButton
        onClick={handleCreate}
        disabled={isLoading || studyReady || isFetching || isUploading}
        data-disabled={isLoading || studyReady || isFetching || isUploading}
      >
        {isLoading || isFetching
          ? 'CREATING…'
          : isUploading
            ? 'UPLOADING DICOMS…'
            : studyReady
              ? 'STUDY EXISTS'
              : 'CREATE A STUDY'}
      </StudyButton>

      {/* Show spinner while uploading */}
      {isUploading && (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <LoadingSpinner />
          <LoadingMessage>Uploading DICOM files…</LoadingMessage>
        </div>
      )}

      {/* Only show ScanSection after upload is done */}
      {studyReady && !isUploading && (
        <div style={{ marginTop: '3rem' }}>
          <ScanSection studyReady={studyReady} name={name} />
        </div>
      )}
    </EncephalonContainer>
  );
};

export default EncephalonSection;
